import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { Button } from 'components/ui/button';
import { useModal } from 'modules/modals/ModalProvider';
import { IBomState, ICreateBomItemResponse } from 'modules/billOfMaterials/types';
import { ReactSelect, TextInput, TextArea } from 'ui';
import {
  useCreateBomItemMutation,
  useUpdateBomItemMutation,
  useGetAllDrawingsQuery,
} from 'modules/billOfMaterials/bomApi';
import { handleApiCall } from 'utils/helpers';
import { showErrorMessage, showSuccessMessage } from 'modules/alert/utils';
import { NOTIFICATION } from 'modules/alert/constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchema } from 'utils/validation';
import * as Layout from '../Layout';

interface IAddPart {
  threadId: number;
  bomState: IBomState;
  handleBomState: (newState: IBomState) => void;
  part: ICreateBomItemResponse;
  defaultDrawing: number;
}

interface IBomPartFormValues {
  drawing?: number;
  part_number: number | string;
  item_number: number | string;
  description?: string;
  manufacturer?: string;
  quantity: string;
  unit: string;
}

const validation = (bomItems: ICreateBomItemResponse[], currentPartId: number) =>
  yup.object({
    part_number: yup
      .string()
      .required()
      .test((value) => value !== undefined && value !== null && value.toString().trim() !== ''),
    item_number: yup
      .string()
      .required()
      .test('unique-item-number', 'This item number already exists in the BOM.', (value) => {
        if (!value || !bomItems) return true;

        const duplicate = bomItems?.some((item: ICreateBomItemResponse) => {
          if (currentPartId && item.id === currentPartId) {
            return false;
          }
          return String(item.item_number) === String(value);
        });

        return !duplicate;
      }),
    description: validationSchema.textArea(),
    manufacturer: validationSchema.string(),
    quantity: validationSchema.stringRequired(),
    unit: validationSchema.stringRequired(),
  });

const AddPart: React.FC<IAddPart> = ({
  threadId,
  bomState,
  handleBomState,
  part,
  defaultDrawing,
}) => {
  const { close } = useModal();
  const { data: drawings } = useGetAllDrawingsQuery({ thread: threadId });

  const filteredDrawings = drawings?.results?.filter(
    (drawing) => drawing.id !== bomState.bom?.assembly_drawing
  );

  const drawingOptions = filteredDrawings?.map((drawing) => ({
    label: drawing.title,
    value: drawing.id.toString(),
  }));

  const [createBomItem] = useCreateBomItemMutation();
  const [updateBomItem] = useUpdateBomItemMutation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IBomPartFormValues>({
    defaultValues: {
      drawing: part?.drawing || defaultDrawing || undefined,
      part_number: part?.part_number || '',
      item_number: part?.item_number || '',
      description: part?.description || '',
      manufacturer: part?.manufacturer || '',
      quantity: part?.quantity ? parseFloat(part?.quantity).toString() : '1',
      unit: part?.unit || 'Each',
    },
    // @ts-expect-error
    resolver: yupResolver(validation(bomState.bom?.items || [], part?.id)),
  });

  const onSubmit = async (values: IBomPartFormValues) => {
    if (!bomState.bom) return;

    const payload = {
      bom: bomState.bom.id,
      part_number: String(values.part_number),
      item_number: Number(values.item_number),
      drawing: values.drawing ? Number(values.drawing) : undefined,
      description: values.description,
      manufacturer: values.manufacturer,
      quantity: values.quantity,
      unit: values.unit,
    };

    const res = part
      ? await updateBomItem({ id: part.id, ...payload })
      : await createBomItem(payload);

    handleApiCall(
      res,
      () => showErrorMessage(NOTIFICATION.BOM_ITEM_FAILURE),
      async () => {
        if ('data' in res && res.data) {
          const items = bomState.bom?.items || [];

          const finalItems = part
            ? items.map((item) => (item.id === part.id ? res.data : item))
            : [...items, res.data];

          handleBomState({
            ...bomState,
            bom: {
              ...bomState.bom!,
              items: finalItems,
            },
          });
        }
        showSuccessMessage(NOTIFICATION.BOM_ITEM_SUCCESS);
        close();
      }
    );
  };

  return (
    <div>
      <form className="relative mb-4 flex h-full flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div className="me-3 ms-1 flex flex-col gap-3">
            <TextInput
              name="item_number"
              required
              register={register}
              label="Item Number:"
              error={errors.item_number}
              className="!border !border-input !bg-background !text-white"
              data-cy="part-item-number"
            />
            <TextInput
              name="part_number"
              required
              register={register}
              label="Part Number:"
              error={errors.part_number}
              className="!border !border-input !bg-background !text-white"
              data-cy="part-part-number"
            />
            <TextInput
              name="manufacturer"
              register={register}
              label="Manufacturer:"
              error={errors.manufacturer}
              className="!border !border-input !bg-background !text-white"
              data-cy="part-manufacturer"
            />
            <TextInput
              name="quantity"
              required
              register={register}
              label="Quantity:"
              error={errors.quantity}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              step={1}
              min={1}
              className="!border !border-input !bg-background !text-white"
              data-cy="part-quantity"
            />
            <TextInput
              register={register}
              name="unit"
              type="text"
              label="Units:"
              required
              error={errors.unit}
              className="!border !border-input !bg-background !text-white"
              data-cy="part-unit"
            />
            <TextArea
              name="description"
              register={register}
              label="Description:"
              resize="none"
              error={errors.description}
              className="!border !border-input !bg-background !text-white"
              data-cy="part-description"
            />
            <Layout.SelectWrapper>
              <Controller
                control={control}
                name="drawing"
                render={({ field }) => {
                  const selectedOption = field.value
                    ? drawingOptions?.find((opt) => opt.value === String(field.value))
                    : undefined;

                  return (
                    <ReactSelect
                      label="Drawing"
                      id="drawing-select"
                      options={drawingOptions}
                      placeholder="None"
                      isCreatable={false}
                      value={selectedOption}
                      onChange={(option) =>
                        field.onChange(option ? Number(option.value) : undefined)
                      }
                      menuDirection="top"
                      isMulti={false}
                    />
                  );
                }}
              />
            </Layout.SelectWrapper>
          </div>
        </div>
        <div className="me-3 mt-auto flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit">Save Part</Button>
        </div>
      </form>
    </div>
  );
};

export default AddPart;
